import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { RouteType } from "src/app/models/enum";
import { FeedbackAnswerPayload, FeedbackTemplateQuestion } from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { feedbackQuestionNeedsAnswer, feedbackQuestionNeedsRating } from "../feedback-question.utils";
import { FeedbackResponseDetail } from "../feedback.types";

@Component({
	selector: "app-my-feedback-detail",
	templateUrl: "./my-feedback-detail.component.html",
	styleUrls: ["./my-feedback-detail.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class MyFeedbackDetailComponent implements OnInit, OnDestroy {
	readonly routeType = RouteType;
	submissionId = "";
	response: FeedbackResponseDetail | null = null;
	pageIndex = 0;
	readonly pageSize = 10;

	private subs = new Subscription();

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private httpService: HttpService,
		private loaderService: LoaderService,
		private sharedService: SharedService,
		private roleService: RoleService,
		private cdr: ChangeDetectorRef,
	) {}

	get listLabel(): string {
		return this.roleService.hasPilotView || this.roleService.hasInstructorView ? "Feedback History" : "Feedback";
	}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.MY_FEEDBACK);
		this.subs.add(
			this.route.paramMap.subscribe((params) => {
				this.submissionId = params.get("submissionId") ?? "";
				this.pageIndex = 0;
				this.response = null;
				if (this.submissionId) {
					this.loadResponse();
				}
			}),
		);
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
	}

	get questions(): FeedbackTemplateQuestion[] {
		return this.response?.template?.questions ?? [];
	}

	needsRating(question: FeedbackTemplateQuestion): boolean {
		return feedbackQuestionNeedsRating(question);
	}

	needsAnswer(question: FeedbackTemplateQuestion): boolean {
		return feedbackQuestionNeedsAnswer(question);
	}

	/** Tied feedback already identifies the assessment. Pilots should not also see the feedback template name. */
	get showTemplateName(): boolean {
		if (!this.response) return false;
		return !(this.roleService.hasPilotView && this.response.isTied);
	}

	get pagedQuestions(): FeedbackTemplateQuestion[] {
		const start = this.pageIndex * this.pageSize;
		return this.questions.slice(start, start + this.pageSize);
	}

	get totalPages(): number {
		return Math.max(1, Math.ceil(this.questions.length / this.pageSize));
	}

	get pageNumbers(): number[] {
		return Array.from({ length: this.totalPages }, (_, index) => index);
	}

	get globalQuestionOffset(): number {
		return this.pageIndex * this.pageSize;
	}

	loadResponse(): void {
		this.loaderService.setLoading(true);
		this.subs.add(
			this.httpService.getMyFeedback(this.submissionId, this.roleService.getUserRole()).subscribe({
				next: (resp) => {
					this.response = (resp?.data ?? resp) as FeedbackResponseDetail;
					this.loaderService.setLoading(false);
					this.cdr.detectChanges();
				},
				error: () => this.loaderService.setLoading(false),
			}),
		);
	}

	answerFor(questionId: string): FeedbackAnswerPayload | undefined {
		return (this.response?.answers ?? []).find((answer) => answer.questionId === questionId);
	}

	questionNumber(index: number): number {
		return this.globalQuestionOffset + index + 1;
	}

	prevPage(): void {
		if (this.pageIndex > 0) this.pageIndex -= 1;
	}

	nextPage(): void {
		if (this.pageIndex < this.totalPages - 1) this.pageIndex += 1;
	}

	back(): void {
		this.router.navigate([RouteType.MY_FEEDBACK]);
	}
}
